import type { AxiosInstance } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/contexts/AxiosContext";
import type { LocationDetailsResponse, MutableEntityListItem } from "@/types/project";
import { getErrorMessage, type MutableEntityReorderBody, throwLockAwareError } from "@/api/apiClient";

/** Request body for location POST — same as GET payload but never includes `lock` (see rest-api/readme.md). */
export type LocationUpdateBody = Omit<LocationDetailsResponse, "lock">;

type ProjectLocationIds = Readonly<{ projectId: string; locationId: string }>;

// --- Location API — rest-api/readme.md ---

const fetchLocationList = async (axiosClient: AxiosInstance, projectId: string): Promise<readonly MutableEntityListItem[]> => {
  try {
    const { data } = await axiosClient.get<readonly MutableEntityListItem[]>(`/${encodeURIComponent(projectId)}/location`);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when listing locations."));
  }
};

const fetchLocation = async (axiosClient: AxiosInstance, projectId: string, locationId: string): Promise<LocationDetailsResponse> => {
  try {
    const { data } = await axiosClient.get<LocationDetailsResponse>(`/${encodeURIComponent(projectId)}/location/${encodeURIComponent(locationId)}`);
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when loading location."));
  }
};

const updateLocation = async (
  axiosClient: AxiosInstance,
  projectId: string,
  locationId: string,
  body: LocationUpdateBody
): Promise<{ status: "ok" }> => {
  try {
    const { data } = await axiosClient.post<{ status: "ok" }>(`/${encodeURIComponent(projectId)}/location/${encodeURIComponent(locationId)}`, body);
    return data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when updating location.");
  }
};

const deleteLocation = async (axiosClient: AxiosInstance, projectId: string, locationId: string): Promise<{ status: "ok" }> => {
  try {
    const { data } = await axiosClient.delete<{ status: "ok" }>(`/${encodeURIComponent(projectId)}/location/${encodeURIComponent(locationId)}`);
    return data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when deleting location.");
  }
};

const reorderLocation = async (
  axiosClient: AxiosInstance,
  projectId: string,
  locationId: string,
  body: MutableEntityReorderBody
): Promise<{ status: "ok" }> => {
  try {
    const { data } = await axiosClient.patch<{ status: "ok" }>(`/${encodeURIComponent(projectId)}/location/${encodeURIComponent(locationId)}`, body);
    return data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when reordering location.");
  }
};

// ===== React Query hooks =====

export const useLocationList = (projectId: string | null) => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["locations", projectId],
    queryFn: async () => fetchLocationList(axiosClient, projectId!),
    enabled: typeof projectId === "string" && projectId.length > 0,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useLocation = (projectId: string | null, locationId: string | null) => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["location", projectId, locationId],
    queryFn: async () => fetchLocation(axiosClient, projectId!, locationId!),
    enabled: typeof projectId === "string" && projectId.length > 0 && typeof locationId === "string" && locationId.length > 0,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useUpdateLocation = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly locationId: string; readonly body: LocationUpdateBody }) {
      return updateLocation(axiosClient, vars.projectId, vars.locationId, vars.body);
    },
    onSuccess(_, vars) {
      void queryClient.invalidateQueries({ queryKey: ["location", vars.projectId, vars.locationId] });
      void queryClient.invalidateQueries({ queryKey: ["locations", vars.projectId] });
      void queryClient.invalidateQueries({ queryKey: ["project", vars.projectId] });
    }
  });
};

export const useDeleteLocation = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: ProjectLocationIds) => deleteLocation(axiosClient, vars.projectId, vars.locationId),
    onSuccess(_, vars) {
      void queryClient.removeQueries({ queryKey: ["location", vars.projectId, vars.locationId] });
      void queryClient.invalidateQueries({ queryKey: ["locations", vars.projectId] });
      void queryClient.invalidateQueries({ queryKey: ["project", vars.projectId] });
    }
  });
};

export const useReorderLocation = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly locationId: string; readonly body: MutableEntityReorderBody }) {
      return reorderLocation(axiosClient, vars.projectId, vars.locationId, vars.body);
    },
    onSuccess(_, vars) {
      void queryClient.invalidateQueries({ queryKey: ["location", vars.projectId, vars.locationId] });
      void queryClient.invalidateQueries({ queryKey: ["locations", vars.projectId] });
      void queryClient.invalidateQueries({ queryKey: ["project", vars.projectId] });
    }
  });
};
